import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Where the floor goes, and what holds its place until it arrives (#942).
 *
 * The floor shows the room's mixing rule, and that rule reaches the scene's
 * ring — part of the room the landing does not carry before first paint
 * (`tests/perf/bundle.perf.test.ts`). So the words are drawn by the server and
 * the floor is fetched when it comes near the viewport, behind a box of its
 * own size, so nothing below it moves when it lands.
 */
const LandingFloor = lazy(() => import("./LandingFloor"));

const BOX =
  "mx-auto aspect-[4/5] max-w-5xl rounded-3xl border border-border bg-card sm:aspect-[16/10]";

function useNear() {
  const anchor = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const node = anchor.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setNear(true);
        observer.disconnect();
      },
      { rootMargin: "600px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return { anchor, near };
}

export function LandingFloorSlot() {
  const { t } = useTranslation();
  const { anchor, near } = useNear();

  return (
    <section
      id="spatial"
      data-testid="landing-spatial"
      className="mx-auto w-full max-w-6xl px-4 py-20"
    >
      <div className="mx-auto mb-10 max-w-2xl text-center">
        <span className="mb-3 inline-block text-xs font-semibold tracking-[0.14em] text-primary uppercase">
          {t("landing.floor.eyebrow")}
        </span>
        <h2 className="text-balance text-xl font-bold tracking-tight">
          {t("landing.floor.title")}
        </h2>
        <p className="mt-4 text-pretty text-lg text-muted-foreground">
          {t("landing.floor.body")}
        </p>
      </div>
      <div ref={anchor}>
        {near ? (
          <Suspense fallback={<div aria-hidden="true" className={BOX} />}>
            <LandingFloor />
          </Suspense>
        ) : (
          <div aria-hidden="true" className={BOX} />
        )}
      </div>
    </section>
  );
}
